import { useEffect, useState } from "react";
import { useT } from "../i18n/index.tsx";
import { useSearchParams } from "react-router";
import type { AssetInfo } from "../lib/assets.ts";
import { chainName, statusLabel, utcDay } from "../lib/format.ts";
import { useDebounced } from "../lib/hooks.ts";
import { IconClose, IconSearch } from "./Icons.tsx";

/** Filters live in the URL (?q=&status=&asset=) so links like "Payouts waiting for approval" work. */
export function useUrlFilters(keys: string[]) {
  const [params, setParams] = useSearchParams();
  const values = Object.fromEntries(keys.map((k) => [k, params.get(k) ?? ""])) as Record<string, string>;
  const setMany = (kv: Record<string, string>) => {
    setParams(
      (p) => {
        const n = new URLSearchParams(p);
        for (const [k, v] of Object.entries(kv)) {
          if (v) n.set(k, v);
          else n.delete(k);
        }
        return n;
      },
      { replace: true },
    );
  };
  const set = (k: string, v: string) => setMany({ [k]: v });
  return { values, set, setMany };
}

export function FilterBar({
  q, onQ, status, onStatus, statuses, asset, onAsset, assets, searchLabel, hideStatus, hint,
}: {
  q: string; onQ: (v: string) => void;
  status: string; onStatus: (v: string) => void; statuses: string[];
  asset: string; onAsset: (v: string) => void; assets: AssetInfo[];
  searchLabel: string; hideStatus?: boolean; hint?: string;
}) {
  const c = useT().common;
  const [text, setText] = useState(q);
  const debounced = useDebounced(text.trim(), 400);
  useEffect(() => setText(q), [q]);
  useEffect(() => {
    if (debounced !== q) onQ(debounced);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced]);

  return (
    <div className="filters" role="search">
      <div className="search">
        <IconSearch size={18} />
        <label htmlFor="flt-q" className="sr-only">{searchLabel}</label>
        <input
          id="flt-q" type="search" placeholder={searchLabel} value={text} spellCheck={false} autoComplete="off"
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") onQ(text.trim()); }}
          aria-describedby={hint ? "flt-q-hint" : undefined}
        />
        {text && (
          <button type="button" className="icon-btn search-clear" onClick={() => { setText(""); onQ(""); }} aria-label={c.clearSearch}>
            <IconClose size={16} />
          </button>
        )}
      </div>
      {!hideStatus && (
        <div className="select-wrap">
          <label htmlFor="flt-status" className="sr-only">{c.status}</label>
          <select id="flt-status" value={status} onChange={(e) => onStatus(e.target.value)}>
            <option value="">{c.allStatuses}</option>
            {statuses.map((s) => <option key={s} value={s}>{statusLabel(s)}</option>)}
          </select>
        </div>
      )}
      {hint && <p id="flt-q-hint" className="filter-hint">{hint}</p>}
      <div className="select-wrap">
        <label htmlFor="flt-asset" className="sr-only">{c.asset}</label>
        <select id="flt-asset" value={asset} onChange={(e) => onAsset(e.target.value)}>
          <option value="">{c.allAssets}</option>
          {assets.map((a) => <option key={a.asset} value={a.asset}>{a.token} · {chainName(a.chain)}</option>)}
        </select>
      </div>
    </div>
  );
}

const PRESETS: { key: string; label: "today" | "days7" | "days30"; from: () => string }[] = [
  { key: "today", label: "today", from: () => utcDay(0) },
  { key: "7d", label: "days7", from: () => utcDay(-6) },
  { key: "30d", label: "days30", from: () => utcDay(-29) },
];

/**
 * Date range as UTC days (YYYY-MM-DD, `to` inclusive): All time / Today / 7 days / 30 days / Custom.
 * Presets set only `from` (up to now); Custom shows two date inputs.
 */
export function DateRange({ from, to, onChange }: { from: string; to: string; onChange: (from: string, to: string) => void }) {
  const c = useT().common;
  const preset = !to ? (PRESETS.find((p) => p.from() === from)?.key ?? (from ? "custom" : "all")) : "custom";
  const [custom, setCustom] = useState(preset === "custom");
  const active = custom ? "custom" : preset;
  return (
    <div className="date-range">
      <div className="seg" role="group" aria-label={c.dateRange}>
        <button type="button" aria-pressed={active === "all"} onClick={() => { setCustom(false); onChange("", ""); }}>{c.allTime}</button>
        {PRESETS.map((p) => (
          <button key={p.key} type="button" aria-pressed={active === p.key} onClick={() => { setCustom(false); onChange(p.from(), ""); }}>{c[p.label]}</button>
        ))}
        <button type="button" aria-pressed={active === "custom"} onClick={() => { setCustom(true); if (!from) onChange(utcDay(-6), utcDay(0)); }}>{c.custom}</button>
      </div>
      {active === "custom" && (
        <div className="date-inputs">
          <label>
            <span>{c.from}</span>
            <input type="date" value={from} max={to || utcDay(0)} onChange={(e) => onChange(e.target.value, to)} />
          </label>
          <label>
            <span>{c.to}</span>
            <input type="date" value={to} min={from || undefined} max={utcDay(0)} onChange={(e) => onChange(from, e.target.value)} />
          </label>
          <span className="muted small">{c.utcDays}</span>
        </div>
      )}
    </div>
  );
}
